import type { WarHits } from './war';

/**
 * The faction as the Omni-Torn Discord bot watches it (apps/discord/src/modules/faction/live.ts
 * builds it): war, the enemy's members with their hospital timers and flights, push readings, our
 * members, chain, organised crimes, the armory items kept stocked, open retals. The server hands it
 * to the faction's own members (`GET /api/faction/live` → `{ live, shared }`); the extension and the
 * website show it. Times are unix seconds.
 */

export interface LiveMember {
  id: number;
  name: string;
  level: number;
  activity: 'online' | 'idle' | 'offline';
  /** Unix s. */
  lastAction: number;
  state: string;
  description: string;
  /** Unix s the hospital/jail stay ends. */
  until: number | null;
  /** Their revive setting ("Everyone", "Friends & faction", "No one"), when Torn's list says. */
  revive?: string | null;
}

export interface LiveTrip {
  id: number;
  name: string;
  destination: string;
  leg: 'out' | 'back' | 'there';
  /** Landing window (unix s); null abroad or "any moment". */
  earliest: number | null;
  latest: number | null;
  exact: boolean;
  /** Unix s: first seen on this leg. */
  since: number;
}

export interface LivePush {
  at: number;
  level: 'quiet' | 'rising' | 'push';
  online: number;
  active: number;
  usual: number | null;
  ready: number;
  reasons: string[];
  woke: Array<{ id: number; name: string; at: number }>;
  outEarly: Array<{ id: number; name: string; at: number }>;
  landed: Array<{ id: number; name: string; at: number }>;
}

export interface PushEvent {
  at: number;
  level: 'rising' | 'push' | 'calm';
  reasons: string[];
  online: number;
  usual: number | null;
}

export interface LiveView {
  factionId: number;
  /** Unix s. */
  at: number;
  /** Why something is missing (no key, Torn error), if anything. */
  problem: string | null;
  /** A Discord server is tied to this faction with Faction alerts on. */
  discord: boolean;
  war: { id: number; start: number; end: number | null; target: number | null; factions: Array<{ id: number; name: string; score: number; chain: number | null }> } | null;
  enemy: { id: number; name: string; manual: boolean } | null;
  enemyAt: number | null;
  enemyMembers: LiveMember[];
  trips: LiveTrip[];
  push: LivePush | null;
  pushes: PushEvent[];
  membersAt: number | null;
  members: Array<LiveMember & { position: string | null; inOc: boolean | null; landsAt: number | null }>;
  chain: { current: number; max: number | null; timeout: number; cooldown: number; start: number | null; at: number } | null;
  oc: {
    at: number;
    crimes: Array<{ id: number; name: string; difficulty: number | null; status: string; readyAt: number | null; slots: Array<{ position: string; userId: number | null; name: string | null; passRate: number | null; itemId: number | null; itemAvailable: boolean | null }> }>;
    /** Members out of a crime, and since when the bot knows. */
    out: Array<{ id: number; name: string; since: number }>;
  } | null;
  armory: { at: number; items: Array<{ id: number; name: string; available: number; quantity: number; min: number | null; low: boolean }> } | null;
  retals: Array<{ attacker: { id: number | null; name: string | null }; until: number; defender: string | null }>;
  /** Our members' hits on the enemy in the current ranked war, counted from the faction's attack log (war.ts). */
  warHits?: WarHits | null;
}


/** The server's answer: the bot's view (or "pending" while it takes its first look) and members' shares. */
export interface FactionLiveAnswer {
  live: LiveView | { factionId: number; pending: true };
  shared: Array<{ id: number; name: string; at: number; [k: string]: unknown }>;
}

export function isLiveView(x: unknown): x is LiveView {
  return !!x && typeof x === 'object' && typeof (x as LiveView).at === 'number' && Array.isArray((x as LiveView).enemyMembers);
}

/** A ranked war running now, or starting within `soonSec`. */
export function warOn(v: LiveView, now: number, soonSec = 30 * 60): boolean {
  return !!v.war && v.war.start - soonSec <= now && (!v.war.end || v.war.end > now);
}

/** One line about the enemy's activity: "Pushing — 9 woke up in 5 min · 23 online (usually 12)". */
export function pushLine(v: LiveView): string | null {
  const p = v.push;
  if (!p || !v.enemy) return null;
  const head = p.level === 'push' ? `${v.enemy.name} is pushing` : p.level === 'rising' ? `${v.enemy.name} is getting busy` : `${v.enemy.name} is quiet`;
  const usual = p.usual !== null ? ` (usually ${Math.round(p.usual)})` : '';
  const woke = p.woke.length ? ` · ${p.woke.length} woke up in 5 min` : '';
  return `${head} — ${p.online} online${usual}${woke} · ${p.ready} can attack`;
}

/** When an enemy (or our member) lands, from the bot's watch: the window, else null. */
export function liveLanding(v: LiveView, id: number): { earliest: number; latest: number; exact: boolean } | null {
  const t = v.trips.find((x) => x.id === id);
  if (t && t.earliest !== null && t.latest !== null) return { earliest: t.earliest, latest: t.latest, exact: t.exact };
  const m = v.members.find((x) => x.id === id);
  return m?.landsAt ? { earliest: m.landsAt, latest: m.landsAt, exact: true } : null;
}
